import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import { Formik, FormikHelpers, useField } from 'formik';
import {
  CompanyPageCompanyFragment,
  CompanyRfuModelType,
  useCompanyPageUpdateCompanyMutation,
} from 'generated/graphql';
import { useState } from 'react';
import {
  enableDisableRadioButtonOptions,
  EnableDisableValue,
} from 'views/Company/CompanyView/CompanyView.helpers';
import {
  dataTestId,
  rfuModelTypeRadioButtonOptions,
} from 'views/Company/CompanyView/components/RoleSkillsProfileSection/RoleSkillsProfileSection.helpers';
import {
  Button,
  EditFormWrapper,
  FormikRadioButtonGroup,
  InlineNotification,
  NotificationType,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './RoleSkillsProfileSection.module.scss';

// Marks the backend rejection raised when RFF soft skills can't be disabled
// because Templates still contain RFF skills. Must stay in sync with the
// BadRequestException message in services/monolith .../company.service.ts.
const RFF_TEMPLATE_ERROR_MARKER = 'Cannot disable RFF soft skills';

type RoleSkillsProfileSectionProps = {
  company: CompanyPageCompanyFragment;
};

export default function RoleSkillsProfileSection({
  company,
}: RoleSkillsProfileSectionProps) {
  const [isEditing, setIsEditing] = useState(false);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  return isEditing ? (
    <RoleSkillsProfileForm
      company={company}
      onCancelClick={handleCancelClick}
      onSave={handleSave}
    />
  ) : (
    <RoleSkillsProfileSectionView
      company={company}
      onEditClick={handleEditClick}
    />
  );
}

type RoleSkillsProfileSectionViewProps = {
  company: CompanyPageCompanyFragment;
  onEditClick: () => void;
};

function RoleSkillsProfileSectionView({
  company,
  onEditClick,
}: RoleSkillsProfileSectionViewProps) {
  return (
    <div className={styles.viewContainer}>
      <div className={styles.header}>
        <h3 className={styles.title}>Role Skills Profile</h3>
        <Button variant="secondary" size="medium" onClick={onEditClick}>
          Edit
        </Button>
      </div>
      <div className={styles.content}>
        <InfoItem
          label="Right for us model type"
          value={
            company.rfuModelType === CompanyRfuModelType.ValueBased
              ? 'Value based'
              : 'Behaviour based'
          }
        />
        <InfoItem
          label="Right for future"
          value={company.areRffSoftSkillsDisabled ? 'Disabled' : 'Enabled'}
        />
      </div>
    </div>
  );
}

type RoleSkillsProfileFormProps = {
  company: CompanyPageCompanyFragment;
  onCancelClick: () => void;
  onSave: () => void;
};

type RoleSkillsProfileFormValues = {
  rfuModelType: CompanyRfuModelType;
  rffSoftSkills: string;
};

type NotificationVisibility = Partial<
  Record<keyof RoleSkillsProfileFormValues, boolean>
>;

function RoleSkillsProfileForm({
  company,
  onCancelClick,
  onSave,
}: RoleSkillsProfileFormProps) {
  const { handleMsgType } = useNotification();

  const [editCompanyMutation] = useCompanyPageUpdateCompanyMutation();
  const [notificationVisibility, setNotificationVisibility] =
    useState<NotificationVisibility>({});
  const [rffTemplateError, setRffTemplateError] = useState<string>();

  const handleSave = (
    values: RoleSkillsProfileFormValues,
    { resetForm }: FormikHelpers<RoleSkillsProfileFormValues>
  ) => {
    editCompanyMutation({
      variables: {
        id: company.id,
        name: company.name,
        isRoleRequirementsEnabled: true,
        rfuModelType: values.rfuModelType,
        areRffSoftSkillsDisabled:
          values.rffSoftSkills === EnableDisableValue.Disabled,
      },
      onCompleted: () => {
        setRffTemplateError(undefined);
        handleMsgType({
          type: TNotification.success,
          title: 'Role Skills Profile settings updated.',
          dismissable: true,
        });
        setNotificationVisibility({});
        onSave();
      },
      onError: error => {
        const templateError = error.graphQLErrors?.find(graphQLError =>
          graphQLError.message.includes(RFF_TEMPLATE_ERROR_MARKER)
        );
        if (templateError) {
          // The backend rejected disabling RFF because Templates still contain
          // RFF skills. Surface the explanation inline (it names the Templates)
          // and revert the radio to its saved state instead of a generic toast.
          setRffTemplateError(templateError.message);
          resetForm();
          return;
        }
        handleMsgType({
          type: TNotification.error,
          title: 'Role Skills Profile settings could not be updated',
          message: 'Something went wrong. Please try again.',
          dismissable: true,
        });
      },
    });
  };

  const initialValues = {
    rfuModelType: company.rfuModelType,
    rffSoftSkills: company.areRffSoftSkillsDisabled
      ? EnableDisableValue.Disabled
      : EnableDisableValue.Enabled,
  };

  const handleChangeRadioButtonGroup = (_value: string, id: string) => {
    setRffTemplateError(undefined);
    setNotificationVisibility(prev => ({
      ...prev,
      [id]: true,
    }));
  };

  return (
    <Formik<RoleSkillsProfileFormValues>
      initialValues={initialValues}
      onSubmit={handleSave}
    >
      {({ submitForm }) => (
        <EditFormWrapper
          onCancelClick={onCancelClick}
          onSaveClick={submitForm}
          title="Role Skills Profile"
          formId="role-skills-profile-form"
          hasResetAllButton={false}
          data-testid={dataTestId}
        >
          <form
            onSubmit={e => e.preventDefault()}
            id="role-skills-profile-form-element"
            className={styles.form}
          >
            <div>
              <FormikRadioButtonGroup
                id="rfuModelType"
                name="rfuModelType"
                options={rfuModelTypeRadioButtonOptions}
                layout="horizontal"
                label="Right for us model type"
                data-testid={`${dataTestId}__rfu-model-type-radio-group`}
                useFormikField={useField}
                className={styles.radioGroup}
                onChange={handleChangeRadioButtonGroup}
              />
              {notificationVisibility.rfuModelType && (
                <InlineNotification
                  notificationType={NotificationType.NEUTRAL}
                  className={styles.inlineNotification}
                >
                  When a company&#39;s RFU model type is <b>values based</b>,
                  all of the company&#39;s values will be presented in Role
                  Requirements without AI suggestions.
                  <br />
                  <br />
                  When a company&#39;s RFU model type is <b>behaviour based</b>,
                  AI will suggest a refined list of values aligned with the
                  recommended range if the number of values the company has
                  exceeds that range.
                </InlineNotification>
              )}
            </div>
            <div>
              <FormikRadioButtonGroup
                id="rffSoftSkills"
                name="rffSoftSkills"
                options={enableDisableRadioButtonOptions}
                layout="horizontal"
                label="Right for future"
                data-testid={`${dataTestId}__rff-soft-skills-radio-group`}
                useFormikField={useField}
                className={styles.radioGroup}
                onChange={handleChangeRadioButtonGroup}
              />
              {notificationVisibility.rffSoftSkills && (
                <InlineNotification
                  notificationType={NotificationType.NEUTRAL}
                  className={styles.inlineNotification}
                >
                  When this setting is enabled, all company users will be able
                  to access Role Requirements to create Role Skills Profiles and
                  projects using the latest skills taxonomy.
                </InlineNotification>
              )}
              {rffTemplateError && (
                <InlineNotification
                  notificationType={NotificationType.ERROR}
                  className={styles.inlineNotification}
                  dataTestId={`${dataTestId}__rff-soft-skills-error`}
                >
                  <p>{rffTemplateError}</p>
                </InlineNotification>
              )}
            </div>
          </form>
        </EditFormWrapper>
      )}
    </Formik>
  );
}
